import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogOut, Trash2, Mail, Phone, Plus, X } from 'lucide-react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { format } from 'date-fns';

const SERVICE_OPTIONS = [
  { name: "Standard Detail Package", price: 99.99 },
  { name: "Shiny Detail Package", price: 149.99 },
  { name: "Platinum Detail Package", price: 199.99 },
  { name: "Standard Interior Detail", price: 74.99 },
  { name: "Full Interior Detail", price: 174.99 },
  { name: "Quick Wash", price: 24.99 },
];


function AdminDashboard() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showNewBooking, setShowNewBooking] = useState(false);
  const { logout, adminName } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin Dashboard | Wash&WaxWorks";
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings`);
      const data = await res.json();
      setBookings(data);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const deleteBooking = async (id) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/${id}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setBookings(prev => prev.filter(b => b.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete booking:', err);
    }
  };

  const updatePrice = (id, newPrice) => {
    setBookings(prev => prev.map(b => b.id === id ? { ...b, price: newPrice } : b));
  };

  const handleBookingCreated = (newBooking) => {
    setBookings(prev => [newBooking, ...prev]);
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    logout();
    navigate('/admin-login');
  };

  const adminInitial = adminName ? adminName[0].toUpperCase() : '?';
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const todayCount = bookings.filter(b => b.date === todayStr).length;

  const completed = bookings.filter(b => b.status === 'Completed');
  const pending = bookings.filter(b => (b.status || 'Pending') !== 'Completed');
  const totalRevenue = completed.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const pendingRevenue = pending.reduce((sum, b) => sum + (Number(b.price) || 0), 0);

  const filtered = selectedDate
    ? bookings.filter(b => b.date === selectedDate)
    : bookings;

  const sorted = [...filtered].sort((a, b) => {
    const sa = a.status === 'Completed' ? 1 : 0;
    const sb = b.status === 'Completed' ? 1 : 0;
    if (sa !== sb) return sa - sb;
    return (b.date || '').localeCompare(a.date || '') || (b.time || '').localeCompare(a.time || '');
  });

  return (
    <section className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">

        <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {adminInitial}
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Hello, {adminName || 'Admin'}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-sm text-zinc-500">{bookings.length} total bookings</p>
                <span className="w-1 h-1 rounded-full bg-zinc-700 inline-block" />
                {todayCount > 0 ? (
                  <span className="text-xs font-semibold text-violet-400 bg-violet-950 border border-violet-800 rounded-full px-2 py-0.5">
                    {todayCount} today
                  </span>
                ) : (
                  <span className="text-xs text-zinc-600">No bookings today</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowNewBooking(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-violet-600 text-white hover:bg-violet-500 transition-all text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Booking</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-red-400 hover:border-red-900 transition-all text-sm font-medium"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10">
          <StatCard label="Total Revenue" value={`$${totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="emerald" />
          <StatCard label="Pending Revenue" value={`$${pendingRevenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} color="amber" />
          <StatCard label="Pending" value={pending.length} color="amber" />
          <StatCard label="Completed" value={completed.length} color="emerald" />
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 sm:p-6 mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Calendar</h2>
            {selectedDate && (
              <button
                onClick={() => setSelectedDate(null)}
                className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                Clear filter
              </button>
            )}
          </div>
          <div className="dashboard-calendar">
            <Calendar
              onClickDay={(value) => {
                const formatted = format(value, 'yyyy-MM-dd');
                setSelectedDate(prev => prev === formatted ? null : formatted);
              }}
              tileContent={({ date }) => {
                const formatted = format(date, 'yyyy-MM-dd');
                const count = bookings.filter(b => b.date === formatted).length;
                return count > 0 ? (
                  <div className="flex justify-center mt-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                  </div>
                ) : null;
              }}
              tileClassName={({ date }) => {
                const formatted = format(date, 'yyyy-MM-dd');
                return formatted === selectedDate ? 'selected-date' : '';
              }}
              className="!bg-transparent !border-none !w-full"
            />
          </div>
          {selectedDate && (
            <div className="mt-3 text-xs text-zinc-400">
              Showing {sorted.length} booking{sorted.length !== 1 ? 's' : ''} for {selectedDate}
            </div>
          )}
        </div>

        <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl overflow-hidden">
          <div className="px-5 sm:px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
            <h2 className="text-base font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {selectedDate ? `Bookings on ${selectedDate}` : 'All Bookings'}
            </h2>
          </div>

          {loading ? (
            <div className="p-20 text-center text-zinc-600 text-sm">Loading...</div>
          ) : sorted.length === 0 ? (
            <div className="p-20 text-center text-zinc-600 text-sm">No bookings yet.</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="px-6 py-3.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Service</th>
                      <th className="px-6 py-3.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Date & Time</th>
                      <th className="px-6 py-3.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Price</th>
                      <th className="px-6 py-3.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3.5 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/60">
                    {sorted.map(b => (
                      <BookingRow key={b.id} booking={b} onToggle={updateStatus} onDelete={deleteBooking} onPriceUpdate={updatePrice} />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-zinc-800/60">
                {sorted.map(b => (
                  <BookingCard key={b.id} booking={b} onToggle={updateStatus} onDelete={deleteBooking} onPriceUpdate={updatePrice} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

        {showNewBooking && (
          <NewBookingModal
            onClose={() => setShowNewBooking(false)}
            onCreated={handleBookingCreated}
          />
        )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&display=swap');

        .dashboard-calendar .react-calendar {
          background: transparent !important;
          font-family: 'Outfit', sans-serif !important;
          color: #a1a1aa !important;
          border: none !important;
        }
        .dashboard-calendar .react-calendar__navigation button {
          color: #fff !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          border-radius: 0.5rem !important;
          min-width: 36px !important;
        }
        .dashboard-calendar .react-calendar__navigation button:hover {
          background: rgba(255,255,255,0.05) !important;
        }
        .dashboard-calendar .react-calendar__navigation button:disabled {
          background: transparent !important;
        }
        .dashboard-calendar .react-calendar__month-view__weekdays__weekday {
          font-size: 11px !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          color: #52525b !important;
          padding-bottom: 12px !important;
          letter-spacing: 0.05em !important;
        }
        .dashboard-calendar .react-calendar__month-view__weekdays__weekday abbr {
          text-decoration: none !important;
        }
        .dashboard-calendar .react-calendar__month-view__days__day {
          color: #a1a1aa !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          padding: 10px 0 !important;
          border-radius: 0.5rem !important;
        }
        .dashboard-calendar .react-calendar__tile {
          border-radius: 0.5rem !important;
          transition: background 0.15s ease !important;
        }
        .dashboard-calendar .react-calendar__tile:hover {
          background: rgba(139, 92, 246, 0.15) !important;
          color: #fff !important;
        }
        .dashboard-calendar .react-calendar__tile--active,
        .dashboard-calendar .react-calendar__tile.selected-date {
          background: rgba(139, 92, 246, 0.3) !important;
          color: #fff !important;
        }
        .dashboard-calendar .react-calendar__tile--now {
          background: rgba(255,255,255,0.05) !important;
          color: #fff !important;
        }
        .dashboard-calendar .react-calendar__month-view__days__day--neighboringMonth {
          color: #27272a !important;
        }
      `}</style>
    </section>
  );
}

function StatCard({ label, value, color }) {
  const accents = {
    emerald: 'text-emerald-400 border-emerald-500/20',
    amber: 'text-amber-400 border-amber-500/20',
  };
  const accent = accents[color] || accents.emerald;

  return (
    <div className={`bg-zinc-900/60 border border-zinc-800 rounded-xl p-5 sm:p-6`}>
      <div className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">{label}</div>
      <div className={`text-xl sm:text-2xl font-bold tabular-nums ${accent.split(' ')[0]}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status, onClick }) {
  const isCompleted = status === 'Completed';
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-colors cursor-pointer ${
        isCompleted
          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20'
          : 'bg-amber-500/10 text-amber-400 border-amber-500/20 hover:bg-amber-500/20'
      }`}
      title={`Click to mark as ${isCompleted ? 'Pending' : 'Completed'}`}
    >
      {status}
    </button>
  );
}

function EditablePrice({ booking, onPriceUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(booking.price != null ? Number(booking.price).toFixed(2) : '');

  const save = async () => {
    setEditing(false);
    const newPrice = parseFloat(value);
    if (isNaN(newPrice) || newPrice < 0) {
      setValue(booking.price != null ? Number(booking.price).toFixed(2) : '');
      return;
    }
    if (Number(booking.price) === newPrice) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings/${booking.id}/price`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price: newPrice })
      });
      if (res.ok) {
        onPriceUpdate(booking.id, newPrice);
      } else {
        setValue(booking.price != null ? Number(booking.price).toFixed(2) : '');
      }
    } catch {
      setValue(booking.price != null ? Number(booking.price).toFixed(2) : '');
    }
  };

  if (editing) {
    return (
      <input
        type="number"
        step="0.01"
        min="0"
        autoFocus
        className="w-24 px-2 py-1 text-sm bg-zinc-950 border border-violet-500 rounded-md text-zinc-100 focus:outline-none focus:ring-1 focus:ring-violet-500 tabular-nums"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') {
            setValue(booking.price != null ? Number(booking.price).toFixed(2) : '');
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="text-sm text-zinc-300 tabular-nums hover:bg-violet-500/10 hover:text-violet-300 px-2 py-1 rounded-md transition-colors cursor-pointer"
      title="Click to edit price"
    >
      {booking.price != null ? `$${Number(booking.price).toFixed(2)}` : '—'}
    </button>
  );
}

function NewBookingModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', car: '',
    service: '', price: '', date: '', time: '',
    location: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleServiceChange = (serviceName) => {
    const opt = SERVICE_OPTIONS.find(s => s.name === serviceName);
    set('service', serviceName);
    if (opt) set('price', opt.price.toFixed(2));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, price: form.price ? parseFloat(form.price) : null })
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to create booking.');
        return;
      }
      onCreated(data);
      onClose();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full px-3 py-2.5 rounded-lg bg-zinc-950 border border-zinc-700 text-zinc-100 text-sm focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors";
  const labelClass = "block text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800">
          <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>New Booking</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{error}</div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Name</label>
              <input type="text" required className={inputClass} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="John Doe" />
            </div>
            <div>
              <label className={labelClass}>Phone</label>
              <input type="tel" required className={inputClass} value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="(226) 000-0000" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input type="email" required className={inputClass} value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="john@example.com" />
          </div>
          <div>
            <label className={labelClass}>Car Model</label>
            <input type="text" required className={inputClass} value={form.car} onChange={(e) => set('car', e.target.value)} placeholder="e.g. Tesla Model 3" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Service</label>
              <select required className={inputClass} value={form.service} onChange={(e) => handleServiceChange(e.target.value)}>
                <option value="">Select a package</option>
                {SERVICE_OPTIONS.map(s => (
                  <option key={s.name} value={s.name}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Price ($)</label>
              <input type="number" step="0.01" min="0" required className={inputClass} value={form.price} onChange={(e) => set('price', e.target.value)} placeholder="0.00" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" required className={`${inputClass} [color-scheme:dark]`} value={form.date} onChange={(e) => set('date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Time</label>
              <input type="time" required className={`${inputClass} [color-scheme:dark]`} value={form.time} onChange={(e) => set('time', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Location</label>
            <input type="text" required className={inputClass} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="123 Street, London ON" />
          </div>
          <div>
            <label className={labelClass}>Notes (optional)</label>
            <textarea rows={2} className={`${inputClass} resize-none`} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Special instructions..." />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${submitting ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed' : 'bg-violet-600 text-white hover:bg-violet-500'}`}
          >
            {submitting ? 'Creating...' : 'Create Booking'}
          </button>
        </form>
      </div>
    </div>
  );
}

function BookingRow({ booking: b, onToggle, onDelete, onPriceUpdate }) {
  const [confirming, setConfirming] = useState(false);
  const status = b.status || 'Pending';

  return (
    <tr className="hover:bg-zinc-800/30 transition-colors">
      <td className="px-6 py-4">
        <div className="text-sm font-medium text-white">{b.name}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{b.email}</div>
      </td>
      <td className="px-6 py-4 text-sm text-zinc-300">{b.service}</td>
      <td className="px-6 py-4">
        <div className="text-sm text-zinc-300">{b.date}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{b.time}</div>
      </td>
      <td className="px-6 py-4 text-sm text-zinc-300">{b.location || '—'}</td>
      <td className="px-6 py-4">
        <EditablePrice booking={b} onPriceUpdate={onPriceUpdate} />
      </td>
      <td className="px-6 py-4">
        <StatusBadge
          status={status}
          onClick={() => onToggle(b.id, status === 'Pending' ? 'Completed' : 'Pending')}
        />
      </td>
      <td className="px-6 py-4">
        {confirming ? (
          <div className="flex items-center justify-end gap-2">
            <span className="text-xs text-red-400">Delete?</span>
            <button
              onClick={() => { onDelete(b.id); setConfirming(false); }}
              className="px-2.5 py-1 rounded-md text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
            >
              Yes
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="px-2.5 py-1 rounded-md text-xs font-medium bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700 transition-colors"
            >
              No
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1">
            <a
              href={`mailto:${b.email}`}
              title="Email customer"
              className="p-2 rounded-lg text-zinc-500 hover:text-violet-400 hover:bg-violet-500/10 transition-colors"
            >
              <Mail className="w-4 h-4" />
            </a>
            {b.phone && (
              <a
                href={`tel:${b.phone}`}
                title="Call customer"
                className="p-2 rounded-lg text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
              >
                <Phone className="w-4 h-4" />
              </a>
            )}
            <button
              onClick={() => setConfirming(true)}
              title="Delete booking"
              className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

function BookingCard({ booking: b, onToggle, onDelete, onPriceUpdate }) {
  const [confirming, setConfirming] = useState(false);
  const status = b.status || 'Pending';

  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm font-medium text-white">{b.name}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{b.service}</div>
        </div>
        <StatusBadge
          status={status}
          onClick={() => onToggle(b.id, status === 'Pending' ? 'Completed' : 'Pending')}
        />
      </div>
      <div className="flex items-center gap-4 text-xs text-zinc-400 flex-wrap">
        <span>{b.date}</span>
        <span>{b.time}</span>
        <EditablePrice booking={b} onPriceUpdate={onPriceUpdate} />
      </div>
      {b.location && <div className="text-xs text-zinc-500">{b.location}</div>}
      {confirming ? (
        <div className="flex items-center gap-2 pt-1">
          <span className="text-xs text-red-400">Delete this booking?</span>
          <button
            onClick={() => { onDelete(b.id); setConfirming(false); }}
            className="px-2.5 py-1 rounded-md text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
          >
            Yes
          </button>
          <button
            onClick={() => setConfirming(false)}
            className="px-2.5 py-1 rounded-md text-xs font-medium bg-zinc-800 text-zinc-400 border border-zinc-700 hover:bg-zinc-700 transition-colors"
          >
            No
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1 pt-1">
          <a href={`mailto:${b.email}`} title="Email" className="p-2 rounded-lg text-zinc-500 hover:text-violet-400 hover:bg-violet-500/10 transition-colors">
            <Mail className="w-4 h-4" />
          </a>
          {b.phone && (
            <a href={`tel:${b.phone}`} title="Call" className="p-2 rounded-lg text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 transition-colors">
              <Phone className="w-4 h-4" />
            </a>
          )}
          <button onClick={() => setConfirming(true)} title="Delete" className="p-2 rounded-lg text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors ml-auto">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
